import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CREDIT_DEPARTMENTS,
  WORK_ROLES,
  productionAdminService,
  type CreditInput,
  type ProductionInput,
} from '../../../services/productionAdminService';
import adminService, { type ContentItem } from '../../../services/adminService';
import { toDisplayMessage } from '../../../services/studioApi';
import {
  PRODUCTION_FORMATS,
  PRODUCTION_STAGES,
  type Production,
  type ProductionSummary,
} from '../../../types/studio';
import { creditReach } from './creditReach';

/**
 * Entering production credits.
 *
 * `GET /content/{id}/credits` and the player's credits display both shipped, and the data
 * behind them had to be inserted by hand - `src/pages/Admin/` had no Productions section
 * at all, though every endpoint existed and was admin-only.
 *
 * The thing this screen has to get right is not the forms. It is that credits reach a
 * player only when the production is Public *and* the film is attached to it as a work,
 * and both ways of failing that are silent. So every production says where it stands.
 */

const input =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-orange-500';
const label = 'block text-xs font-medium text-gray-700 mb-1';

const humanise = (value: string) =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');

/** Every field, every time: PUT replaces the record, so an omitted field is reset. */
function toInput(production: Production): ProductionInput {
  return {
    title: production.title,
    logline: production.logline ?? '',
    synopsis: production.synopsis ?? '',
    format: production.format,
    stage: production.stage,
    visibility: production.visibility,
    genre: production.genre ?? '',
    posterUrl: production.posterUrl ?? '',
    bannerUrl: production.bannerUrl ?? '',
    trailerUrl: production.trailerUrl ?? '',
    shootLocation: production.shootLocation ?? '',
    shootStartDate: production.shootStartDate ?? '',
    shootEndDate: production.shootEndDate ?? '',
    releaseDate: production.releaseDate ?? '',
    productionYear: production.productionYear,
    runtimeMinutes: production.runtimeMinutes,
    partnerName: production.partnerName ?? '',
    partnerUrl: production.partnerUrl ?? '',
    featured: production.featured,
    openForCasting: production.openForCasting,
  };
}

const BLANK: ProductionInput = {
  title: '',
  format: 'SHORT_FILM',
  stage: 'DEVELOPMENT',
  visibility: 'DRAFT',
};

const ProductionManagement: React.FC = () => {
  const [productions, setProductions] = useState<ProductionSummary[]>([]);
  const [selected, setSelected] = useState<Production | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (keepSlug?: string) => {
    setLoading(true);
    try {
      const page = await productionAdminService.listAll();
      setProductions(page.content ?? []);
      setError(null);
      if (keepSlug) setSelected(await productionAdminService.get(keepSlug));
    } catch (e) {
      setError(toDisplayMessage(e, 'Could not load productions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const open = async (slug: string) => {
    setCreating(false);
    try {
      setSelected(await productionAdminService.get(slug));
      setError(null);
    } catch (e) {
      setError(toDisplayMessage(e, 'Could not open that production'));
    }
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Productions</h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-600">
              A production is what carries the credits shown under a film. Credits reach a
              player only when the production is <strong>Public</strong> and the film is
              attached to it below — each production says where it stands.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setCreating(true); setSelected(null); }}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            New production
          </button>
        </div>

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-[20rem_1fr] lg:items-start">
          <div className="rounded-xl border border-gray-200">
            {loading ? (
              <p className="px-4 py-6 text-sm text-gray-600">Loading…</p>
            ) : productions.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-600">
                No productions yet. Create one to enter the credits for a film.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {productions.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => void open(p.slug)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${
                        selected?.slug === p.slug ? 'bg-gray-50' : ''
                      }`}
                    >
                      <span className="block text-sm font-semibold text-gray-900">{p.title}</span>
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span
                          className={`rounded-full px-2 py-0.5 font-medium ${
                            p.visibility === 'PUBLIC'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {p.visibility === 'PUBLIC' ? 'Public' : 'Draft'}
                        </span>
                        <span>{p.formatLabel || humanise(p.format)}</span>
                        <span>· {p.creditCount} credits</span>
                        <span>· {p.workCount} attached</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            {creating ? (
              <ProductionForm
                heading="New production"
                initial={BLANK}
                onCancel={() => setCreating(false)}
                onSave={async (values) => {
                  const created = await productionAdminService.create(values);
                  setCreating(false);
                  await refresh(created.slug);
                }}
              />
            ) : selected ? (
              <ProductionDetail
                production={selected}
                onChanged={(updated) => { setSelected(updated); void refresh(updated.slug); }}
                onDeleted={() => { setSelected(null); void refresh(); }}
              />
            ) : (
              <p className="rounded-xl border border-gray-200 px-4 py-6 text-sm text-gray-600">
                Choose a production to enter its credits, or create one.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ detail

const ProductionDetail: React.FC<{
  production: Production;
  onChanged: (updated: Production) => void;
  onDeleted: () => void;
}> = ({ production, onChanged, onDeleted }) => {
  const [editing, setEditing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const reach = useMemo(() => creditReach(production), [production]);

  const remove = async () => {
    if (!window.confirm(`Delete "${production.title}"? Its credits go with it.`)) return;
    try {
      await productionAdminService.remove(production.slug);
      onDeleted();
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not delete that production'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{production.title}</h2>
            <p className="text-xs text-gray-600">
              {production.formatLabel || humanise(production.format)} ·{' '}
              {production.stageLabel || humanise(production.stage)}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditing((was) => !was)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50"
            >
              {editing ? 'Close' : 'Edit details'}
            </button>
            <button
              type="button"
              onClick={() => void remove()}
              className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>

        <p
          role="status"
          className={`mt-3 rounded-lg px-3 py-2 text-sm ${
            reach.live ? 'bg-green-50 text-green-900' : 'bg-amber-50 text-amber-900'
          }`}
        >
          {reach.message}
        </p>

        {problem && <p role="alert" className="mt-2 text-sm text-red-700">{problem}</p>}
      </div>

      {editing && (
        <ProductionForm
          heading="Edit production"
          initial={toInput(production)}
          onCancel={() => setEditing(false)}
          onSave={async (values) => {
            const updated = await productionAdminService.update(production.slug, values);
            setEditing(false);
            onChanged(updated);
          }}
        />
      )}

      <CreditsPanel production={production} onChanged={onChanged} />
      <WorksPanel production={production} onChanged={onChanged} />
    </div>
  );
};

// ------------------------------------------------------------------ the form

const ProductionForm: React.FC<{
  heading: string;
  initial: ProductionInput;
  onCancel: () => void;
  onSave: (values: ProductionInput) => Promise<void>;
}> = ({ heading, initial, onCancel, onSave }) => {
  const [values, setValues] = useState<ProductionInput>(initial);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const set = <K extends keyof ProductionInput>(key: K, value: ProductionInput[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!values.title.trim()) {
      setProblem('A title is required.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await onSave({ ...values, title: values.title.trim() });
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not save the production'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 px-4 py-4">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">{heading}</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={label} htmlFor="production-title">Title</label>
          <input id="production-title" className={input} value={values.title}
                 onChange={(e) => set('title', e.target.value)} />
        </div>

        <div className="sm:col-span-2">
          <label className={label} htmlFor="production-logline">Logline</label>
          <input id="production-logline" className={input} value={values.logline ?? ''}
                 onChange={(e) => set('logline', e.target.value)} />
        </div>

        <div>
          <label className={label} htmlFor="production-format">Format</label>
          <select id="production-format" className={input} value={values.format ?? 'SHORT_FILM'}
                  onChange={(e) => set('format', e.target.value)}>
            {PRODUCTION_FORMATS.map((f) => <option key={f} value={f}>{humanise(f)}</option>)}
          </select>
        </div>

        <div>
          <label className={label} htmlFor="production-stage">Stage</label>
          <select id="production-stage" className={input} value={values.stage ?? 'DEVELOPMENT'}
                  onChange={(e) => set('stage', e.target.value)}>
            {PRODUCTION_STAGES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={label} htmlFor="production-visibility">Visibility</label>
          <select id="production-visibility" className={input} value={values.visibility ?? 'DRAFT'}
                  onChange={(e) => set('visibility', e.target.value)}>
            <option value="DRAFT">Draft — nobody sees it, and its credits appear nowhere</option>
            <option value="PUBLIC">Public — listed, and its credits show on attached films</option>
          </select>
        </div>

        <div>
          <label className={label} htmlFor="production-year">Year</label>
          <input id="production-year" type="number" className={input}
                 value={values.productionYear ?? ''}
                 onChange={(e) => set('productionYear', e.target.value ? Number(e.target.value) : null)} />
        </div>

        <div>
          <label className={label} htmlFor="production-runtime">Runtime (minutes)</label>
          <input id="production-runtime" type="number" className={input}
                 value={values.runtimeMinutes ?? ''}
                 onChange={(e) => set('runtimeMinutes', e.target.value ? Number(e.target.value) : null)} />
        </div>

        <div>
          <label className={label} htmlFor="production-genre">Genre</label>
          <input id="production-genre" className={input} value={values.genre ?? ''}
                 onChange={(e) => set('genre', e.target.value)} />
        </div>

        <div>
          <label className={label} htmlFor="production-location">Shoot location</label>
          <input id="production-location" className={input} value={values.shootLocation ?? ''}
                 onChange={(e) => set('shootLocation', e.target.value)} />
        </div>

        <div>
          <label className={label} htmlFor="production-release">Release date</label>
          <input id="production-release" type="date" className={input} value={values.releaseDate ?? ''}
                 onChange={(e) => set('releaseDate', e.target.value)} />
        </div>

        <div>
          <label className={label} htmlFor="production-poster">Poster URL</label>
          <input id="production-poster" className={input} value={values.posterUrl ?? ''}
                 onChange={(e) => set('posterUrl', e.target.value)} />
        </div>

        <div className="sm:col-span-2">
          <label className={label} htmlFor="production-synopsis">Synopsis</label>
          <textarea id="production-synopsis" rows={4} className={input} value={values.synopsis ?? ''}
                    onChange={(e) => set('synopsis', e.target.value)} />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input type="checkbox" checked={Boolean(values.featured)}
                 onChange={(e) => set('featured', e.target.checked)} />
          Featured on the productions hub
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input type="checkbox" checked={Boolean(values.openForCasting)}
                 onChange={(e) => set('openForCasting', e.target.checked)} />
          Open for casting
        </label>
      </div>

      {problem && <p role="alert" className="mt-3 text-sm text-red-700">{problem}</p>}

      <div className="mt-4 flex gap-2">
        <button type="button" onClick={() => void submit()} disabled={saving}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60">
          {saving ? 'Saving…' : 'Save production'}
        </button>
        <button type="button" onClick={onCancel}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-800 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ credits

const BLANK_CREDIT: CreditInput = { creditedName: '', roleName: '', department: 'CAST' };

const CreditsPanel: React.FC<{
  production: Production;
  onChanged: (updated: Production) => void;
}> = ({ production, onChanged }) => {
  const [draft, setDraft] = useState<CreditInput>(BLANK_CREDIT);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const add = async () => {
    if (!draft.creditedName.trim() || !draft.roleName.trim()) {
      setProblem('A credit needs a name and a role.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      onChanged(await productionAdminService.addCredit(production.slug, {
        ...draft,
        creditedName: draft.creditedName.trim(),
        roleName: draft.roleName.trim(),
        memberSlug: draft.memberSlug?.trim() || undefined,
      }));
      setDraft(BLANK_CREDIT);
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not add that credit'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (creditId: string, name: string) => {
    if (!window.confirm(`Remove ${name} from the credits?`)) return;
    try {
      onChanged(await productionAdminService.removeCredit(production.slug, creditId));
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not remove that credit'));
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 px-4 py-4">
      <h3 className="text-sm font-semibold text-gray-900">
        Credits <span className="font-normal text-gray-600">({production.creditCount})</span>
      </h3>
      <p className="mt-1 text-xs text-gray-600">
        Shown on the player grouped by department, in the order below — the same order as an
        end roll.
      </p>

      {production.creditGroups.length === 0 ? (
        <p className="mt-3 text-sm text-gray-600">No credits entered yet.</p>
      ) : (
        <div className="mt-3 space-y-4">
          {production.creditGroups.map((group) => (
            <div key={group.department}>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                {group.departmentLabel || humanise(group.department)}
              </h4>
              <ul className="mt-1 divide-y divide-gray-100">
                {group.credits.map((credit) => (
                  <li key={credit.id} className="flex items-baseline justify-between gap-3 py-1.5">
                    <span className="text-sm text-gray-900">
                      {credit.creditedName}
                      <span className="text-gray-600"> — {credit.roleName}</span>
                      {credit.lead && <span className="ml-2 text-xs text-orange-700">Lead</span>}
                      {credit.memberSlug && (
                        <span className="ml-2 text-xs text-gray-500">linked to a member profile</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => void remove(credit.id, credit.creditedName)}
                      aria-label={`Remove ${credit.creditedName}`}
                      className="text-xs text-red-700 hover:underline"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_10rem_auto] sm:items-end">
        <div>
          <label className={label} htmlFor="credit-name">Name as credited</label>
          <input id="credit-name" className={input} value={draft.creditedName}
                 onChange={(e) => setDraft((d) => ({ ...d, creditedName: e.target.value }))} />
        </div>
        <div>
          <label className={label} htmlFor="credit-role">Role</label>
          <input id="credit-role" className={input} value={draft.roleName}
                 placeholder="Director, or the character played"
                 onChange={(e) => setDraft((d) => ({ ...d, roleName: e.target.value }))} />
        </div>
        <div>
          <label className={label} htmlFor="credit-department">Department</label>
          <select id="credit-department" className={input} value={draft.department}
                  onChange={(e) => setDraft((d) => ({ ...d, department: e.target.value }))}>
            {CREDIT_DEPARTMENTS.map((dep) => (
              <option key={dep.value} value={dep.value}>{dep.label}</option>
            ))}
          </select>
        </div>
        <button type="button" onClick={() => void add()} disabled={saving}
                className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60">
          {saving ? 'Adding…' : 'Add credit'}
        </button>
      </div>

      <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className={label} htmlFor="credit-member">
            Member profile slug (optional — links the credit to their profile page)
          </label>
          <input id="credit-member" className={input} value={draft.memberSlug ?? ''}
                 onChange={(e) => setDraft((d) => ({ ...d, memberSlug: e.target.value }))} />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-gray-800">
          <input type="checkbox" checked={Boolean(draft.lead)}
                 onChange={(e) => setDraft((d) => ({ ...d, lead: e.target.checked }))} />
          Lead
        </label>
      </div>

      {problem && <p role="alert" className="mt-2 text-sm text-red-700">{problem}</p>}
    </section>
  );
};

// ------------------------------------------------------------------ works

const WorksPanel: React.FC<{
  production: Production;
  onChanged: (updated: Production) => void;
}> = ({ production, onChanged }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<ContentItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [role, setRole] = useState('MAIN');
  const [problem, setProblem] = useState<string | null>(null);

  const find = async () => {
    setSearching(true);
    setProblem(null);
    try {
      const page = await adminService.getContentManagement({ search: search.trim(), page_size: 10 });
      setResults(page.content ?? []);
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not search content'));
    } finally {
      setSearching(false);
    }
  };

  const attach = async (contentId: string) => {
    setProblem(null);
    try {
      onChanged(await productionAdminService.attachWork(production.slug, { contentId, role }));
      setResults([]);
      setSearch('');
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not attach that content'));
    }
  };

  const detach = async (workId: string, title: string) => {
    if (!window.confirm(`Detach "${title}"? Its credits stop showing on that film.`)) return;
    try {
      onChanged(await productionAdminService.detachWork(production.slug, workId));
    } catch (e) {
      setProblem(toDisplayMessage(e, 'Could not detach that content'));
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 px-4 py-4">
      <h3 className="text-sm font-semibold text-gray-900">Films attached to this production</h3>
      <p className="mt-1 text-xs text-gray-600">
        This is what connects the credits above to a player page. A credit on a production
        with nothing attached is shown to nobody.
      </p>

      {production.works.length === 0 ? (
        <p className="mt-3 text-sm text-gray-600">Nothing attached yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100">
          {production.works.map((work) => (
            <li key={work.id} className="flex items-baseline justify-between gap-3 py-2">
              <span className="text-sm text-gray-900">
                {work.title}
                <span className="text-gray-600"> — {work.roleLabel || humanise(work.role)}</span>
              </span>
              <button
                type="button"
                onClick={() => void detach(work.id, work.title)}
                aria-label={`Detach ${work.title}`}
                className="text-xs text-red-700 hover:underline"
              >
                Detach
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_12rem_auto] sm:items-end">
        <div>
          <label className={label} htmlFor="work-search">Find content to attach</label>
          <input id="work-search" className={input} value={search} placeholder="Search by title"
                 onChange={(e) => setSearch(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') void find(); }} />
        </div>
        <div>
          <label className={label} htmlFor="work-role">Attach as</label>
          <select id="work-role" className={input} value={role} onChange={(e) => setRole(e.target.value)}>
            {WORK_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <button type="button" onClick={() => void find()} disabled={searching}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-60">
          {searching ? 'Searching…' : 'Search'}
        </button>
      </div>

      {results.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {results.map((item) => (
            <li key={item.id} className="flex items-baseline justify-between gap-3 px-3 py-2">
              <span className="text-sm text-gray-900">
                {item.title}
                <span className="text-xs text-gray-600"> · {humanise(item.contentType)} · {humanise(item.status)}</span>
              </span>
              <button type="button" onClick={() => void attach(item.id)}
                      className="text-xs font-medium text-orange-700 hover:underline">
                Attach
              </button>
            </li>
          ))}
        </ul>
      )}

      {problem && <p role="alert" className="mt-2 text-sm text-red-700">{problem}</p>}
    </section>
  );
};

export default ProductionManagement;
